import { ApolloQueryResult, gql } from '@apollo/client';
import { PayloadAction } from '@reduxjs/toolkit';
import { format } from 'date-fns';
import { ActionPattern, all, call, delay, put, select, spawn, take, takeLatest } from 'redux-saga/effects';

import i18n from '@app/services/translations';

import {
  AutopayMethod,
  NotificationLevel,
  OutageNotification,
  ProductInstanceStatus,
  Query,
  SummarizedWorkOrder,
} from '@mfe/shared/schema-types';

import { selectHasFiber, selectUser } from '../auth';
import { waitForUserData } from '../auth/userSagas';
import { BillingInfoState, selectBillingInfo, setBillingInfo } from '../billingInfo';
import { selectChangePlan } from '../changePlan';
import { fetchHasPendingTransition } from '../changePlan/changePlanSagas';
import { graphqlQuery } from '../graphql';
import { setLocaleState } from '../locale';
import { selectPlanCharacteristics, waitForPlanCharacteristics, waitForUsage } from '../plan';
import { waitForScheduleInstallationDates } from '../scheduleInstallation';
import { displayServiceCallAlert } from '../workOrders';
import {
  Alert,
  AlertAction,
  AlertType,
  DisplayLocation,
  fetchAccountAlerts as fetchAccountAlertsAction,
  resetAlerts,
  setAlerts,
  setBillingAlerts,
} from './alertsSlice';
import { parseAccountAlerts } from './parseAccountAlerts';
import { setBillingPageStatistics, setOverviewPageStatistics } from '../utils';
import { waitForToken } from '../utils/utilsSagas';

const GET_OUTAGE_NOTIFICATIONS = gql`
  query getOutageNotifications {
    getOutageNotifications {
      outages {
        level
        type
      }
    }
  }
`;

export const parseBillingAlerts = (autoPay?: AutopayMethod | null): Alert[] => {
  const alerts: Alert[] = [];

  if (!autoPay) {
    alerts.push({
      level: NotificationLevel.Warning,
      title: i18n.t('Alerts:Billing.NoValidPaymentMethod.title'),
      caption: i18n.t('Alerts:Billing.NoValidPaymentMethod.caption'),
      button: {
        action: AlertAction.AutoPay,
        label: i18n.t('Alerts:Billing.NoValidPaymentMethod.button.text'),
      },
      type: AlertType.NoValidPaymentMethod,
      displayLocation: DisplayLocation.LOCAL,
    });
  } else if (autoPay?.isExpired) {
    alerts.push({
      level: NotificationLevel.Warning,
      title: i18n.t('Alerts:Billing.PaymentMethodExpired.title'),
      caption: i18n.t('Alerts:Billing.PaymentMethodExpired.caption'),
      button: {
        action: AlertAction.AutoPay,
        label: i18n.t('Alerts:Billing.PaymentMethodExpired.button.text'),
      },
      type: AlertType.PaymentMethodExpired,
      displayLocation: DisplayLocation.LOCAL,
    });
  }

  return alerts;
};

export const parseOutages = (data: Partial<ApolloQueryResult<Query>['data']>): Alert[] => {
  const outages = data?.getOutageNotifications?.outages ?? [];

  return outages.map((notification: OutageNotification): Alert => {
    return {
      level: notification.level,
      title: i18n.t(`Alerts:Outage.${notification.type}.title`),
      type: AlertType.Outage,
      displayLocation: DisplayLocation.LOCAL,
    };
  });
};

export const hasSetAutopay = (action: PayloadAction<BillingInfoState['billingInfo']>) =>
  action.type === setBillingInfo.type && Object.keys(action.payload).includes('autoPay');

export function* fetchBillingAlerts() {
  yield call(waitForToken);
  yield take(hasSetAutopay as ActionPattern);

  const {
    billingInfo: { autoPay },
  }: ReturnType<typeof selectBillingInfo> = yield select(selectBillingInfo);

  const billingAlerts: Alert[] = yield call(parseBillingAlerts, autoPay);
  yield put(setBillingAlerts(billingAlerts));
}

export const hasSetSummaryInvoiceStatus = (action: PayloadAction<BillingInfoState['billingInfo']>) =>
  action.type === setBillingInfo.type && Object.keys(action.payload).includes('summaryInvoiceStatus');

export function* fetchAccountAlerts() {
  yield call(waitForToken);
  yield take(hasSetSummaryInvoiceStatus as ActionPattern);
  yield call(waitForPlanCharacteristics);
  yield call(waitForUsage);
  yield call(waitForScheduleInstallationDates);

  const { characteristics, errors: characteristicsErrors }: ReturnType<typeof selectPlanCharacteristics> = yield select(
    selectPlanCharacteristics
  );

  if (characteristics && !characteristicsErrors) {
    const alerts: Alert[] = yield call(parseAccountAlerts);
    yield put(setAlerts(alerts));

    return;
  }
}

export function* fetchAccountErrorAlerts() {
  yield call(waitForToken);
  yield call(waitForUserData);

  const {
    user: { productInstanceStatus, hasErrorProductInstance },
  }: ReturnType<typeof selectUser> = yield select(selectUser);

  if (hasErrorProductInstance) {
    yield call(fetchHasPendingTransition);
  }

  const { hasActivationError } = yield select(selectChangePlan);

  const alerts: Alert[] = [
    {
      level: NotificationLevel.Error,
      title: i18n.t('Alerts:AccountStatus.error.title'),
      caption: i18n.t('Alerts:AccountStatus.error.caption'),
      type: AlertType.AccountStatusError,
      displayLocation: DisplayLocation.GLOBAL,
    },
    {
      level: NotificationLevel.Error,
      title: i18n.t('Alerts:AccountStatus.transitionError.title'),
      caption: i18n.t('Alerts:AccountStatus.transitionError.caption'),
      type: AlertType.AccountStatusTransitionError,
      displayLocation: DisplayLocation.GLOBAL,
    },
  ];

  if (productInstanceStatus === ProductInstanceStatus.Error) {
    yield put(setAlerts([alerts[0]]));
    return;
  }

  if (productInstanceStatus === ProductInstanceStatus.Active && hasActivationError) {
    yield put(setAlerts([alerts[1]]));
    return;
  }
}

export function* fetchOutageAlerts() {
  yield call(waitForToken);
  const data: Query = yield call(graphqlQuery, {
    query: GET_OUTAGE_NOTIFICATIONS,
  });

  const outages = parseOutages(data);

  yield put(setAlerts(outages));
}

function* refetchAllAlerts() {
  yield put(resetAlerts());
  yield call(fetchAccountErrorAlerts);
  yield call(fetchAllAlerts);
}

function* debounceRefetchAccountAlerts() {
  yield delay(1500);
  yield call(fetchAccountAlerts);
}

function* fetchAllAlerts() {
  yield all([
    call(fetchAccountErrorAlerts),
    call(fetchBillingAlerts),
    call(fetchAccountAlerts),
    call(fetchOutageAlerts),
  ]);
}

type DisplayServiceCallAlertAction = PayloadAction<SummarizedWorkOrder>;

function* getServiceCallAlert(action: DisplayServiceCallAlertAction) {
  const {
    user: { productInstanceStatus },
  }: ReturnType<typeof selectUser> = yield select(selectUser);
  const hasFiber: ReturnType<typeof selectHasFiber> = yield select(selectHasFiber);

  const isFiberActive = productInstanceStatus === ProductInstanceStatus.Active && hasFiber;

  if (isFiberActive) {
    return;
  }

  const serviceCallWorkOrder = action.payload;
  const formattedDate = format(new Date(serviceCallWorkOrder?.schedule?.from as string), 'MMMM d');

  const from = new Date(serviceCallWorkOrder?.schedule?.from as string);
  const to = new Date(serviceCallWorkOrder?.schedule?.to as string);

  const serviceCallAlert = {
    level: NotificationLevel.Info,
    title: i18n.t('Alerts:AccountStatus.serviceCall.title'),
    customDescription: `${i18n.t(
      'Alerts:AccountStatus.serviceCall.caption'
    )} <span class="beam-font--bodySmallBold">${i18n.t('Alerts:AccountStatus.serviceCall.dateRange', {
      formattedDate,
      from,
      to,
    })}</span>`,
    button: {
      action: AlertAction.RescheduleServiceCall,
      label: i18n.t('Alerts:AccountStatus.serviceCall.button.text'),
    },
    type: AlertType.ServiceCall,
    displayLocation: DisplayLocation.GLOBAL,
  };

  yield put(setAlerts([serviceCallAlert]));
}

export function* watchAlerts() {
  yield spawn(function* () {
    yield all([
      takeLatest(displayServiceCallAlert.type, getServiceCallAlert),
      takeLatest(setBillingPageStatistics.type, fetchAllAlerts),
      takeLatest(setLocaleState.type, refetchAllAlerts),
      takeLatest(setOverviewPageStatistics.type, fetchAllAlerts),
      takeLatest(fetchAccountAlertsAction.type, debounceRefetchAccountAlerts),
    ]);
  });
}
